import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Send, Eye, EyeOff } from 'lucide-react';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';

const EXAMPLE_PAYLOAD = `{
  "event": "job.completed",
  "timestamp": "2026-02-03T15:30:00.000Z",
  "job": {
    "id": "uuid",
    "job_number": "JOB-3832",
    "status": "completed",
    "job_date": "2026-02-03",
    "invoice_total": 150.00,
    "total_parts_cost": 25.00,
    "total_parts_sell": 35.00,
    "toll_cost": 5.00,
    "payment_status": "paid"
  },
  "customer": {
    "name": "John Smith",
    "phone": "0412345678",
    "email": "john@example.com",
    "postcode": "2000"
  },
  "attribution": {
    "call_id": "3999811730",
    "gclid": "Cj0KCQiA...EALw_wcB",
    "campaign_name": "Google Ads - Sydney",
    "lead_source": "Google Ads",
    "website_source": "plumber-sydney.com.au",
    "tracking_number": "+61866294006"
  },
  "lead": {
    "id": "uuid",
    "lead_number": "LEAD-4521",
    "lead_entry_type": "webhook"
  }
}`;

/** Settings body for the outbound Job Close webhook. */
export function JobCloseWebhookPanel() {
  const { data: company, update, isUpdating } = useCompanyProfile();
  const [showSecret, setShowSecret] = useState(false);
  const [outboundWebhookUrl, setOutboundWebhookUrl] = useState('');
  const [outboundWebhookSecret, setOutboundWebhookSecret] = useState('');

  useEffect(() => {
    if (company) {
      setOutboundWebhookUrl(company.outbound_webhook_url || '');
      setOutboundWebhookSecret(company.outbound_webhook_secret || '');
    }
  }, [company]);

  const enabled = Boolean(company?.outbound_webhook_enabled);

  const handleSaveWebhookConfig = () => {
    update({
      outbound_webhook_url: outboundWebhookUrl || null,
      outbound_webhook_secret: outboundWebhookSecret || null,
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Send job completion data to external systems (Make.com, Zapier, Google Ads, etc.)
      </p>

      <div className="flex items-center justify-between gap-4">
        <div>
          <Label htmlFor="webhook-enabled">Enable Webhook</Label>
          <p className="text-sm text-muted-foreground">
            Automatically send data when jobs are completed
          </p>
        </div>
        <Switch
          id="webhook-enabled"
          checked={enabled}
          onCheckedChange={(checked) => update({ outbound_webhook_enabled: checked })}
          disabled={isUpdating}
        />
      </div>

      {enabled && (
        <>
          <div className="border-t pt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="outbound-webhook-url">Webhook URL</Label>
              <Input
                id="outbound-webhook-url"
                value={outboundWebhookUrl}
                onChange={(e) => setOutboundWebhookUrl(e.target.value)}
                placeholder="https://hook.make.com/xxxxx or https://hooks.zapier.com/xxxxx"
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                The URL that will receive a POST request when jobs are completed
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="outbound-webhook-secret">Webhook Secret (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="outbound-webhook-secret"
                  type={showSecret ? 'text' : 'password'}
                  value={outboundWebhookSecret}
                  onChange={(e) => setOutboundWebhookSecret(e.target.value)}
                  placeholder="Enter a secret for verification"
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowSecret(!showSecret)}
                  title={showSecret ? 'Hide secret' : 'Show secret'}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Sent in the <code className="px-1 py-0.5 bg-muted rounded">x-webhook-secret</code> header for request verification
              </p>
            </div>

            <Button onClick={handleSaveWebhookConfig} disabled={isUpdating} className="w-full sm:w-auto">
              <Send className="h-4 w-4 mr-2" />
              {isUpdating ? 'Saving...' : 'Save Webhook Configuration'}
            </Button>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Example Payload</h4>
            <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">{EXAMPLE_PAYLOAD}</pre>
          </div>
        </>
      )}
    </div>
  );
}
